/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import {
  IconX,
  IconAdjustmentsHorizontal,
  IconClipboardText,
  IconSettings,
} from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";

import NewSelectInput from "../../../components/NewSelectInput";
import NewinputField from "../../../components/NewInput";

import {
  useOrderForwardBulkMutation,
  useOrderReverseMutation,
  useOrderStatusRevertStatusMutation,
  useOrderStatusUpdateStatusMutation,
} from "../../../apis/orderApis";

import { use3PLListData } from "../../../apis/3plApis";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import {
  usePickupLocationClientAddressData,
  usePickRequestMutation,
} from "../../../apis/pickupApis";
import {
  useBulkReverseNaqelShipmentMutation,
  useNaqelBulkForwardMutation,
} from "../../../apis/naqelApis";
import {
  useAramexBulkForwardMutation,
  useAramexReverseShipmentMutation,
} from "../../../apis/aramexApis";
import {
  useJtBulkForwardMutation,
  useCancelJtOrderMutation,
} from "../../../apis/jtExpressApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";

const ActionTypeData = [
  { id: 1, value: "forward", text: "3PL Forward" },
  { id: 2, value: "labelPrint", text: "3PL Label Print" },
  { id: 3, value: "forwardReverse", text: "Forward Reverse" },
  { id: 4, value: "statusRevert", text: "Status Revert" },
  { id: 5, value: "reverseOrder", text: "Reverse Order" },
  { id: 6, value: "generatePickup", text: "Generate Pickup" },
  { id: 7, value: "orderbulkpdf", text: "PDF Generate" },
  { id: 8, value: "returnTo3PL", text: "3PL Return" },
  { id: 9, value: "splitOrder", text: "Split Order" },
  { id: 10, value: "cancelForward3PL", text: "Cancel 3PL Forward" },
  { id: 11, value: "statusUpdate", text: "Status Update" },
];

const ORDER_STATUS_OPTIONS = [
  { id: "NEW_ORDER", value: "NEW_ORDER", text: "New Order" },
  {
    id: "PICKUP_GENERATED",
    value: "PICKUP_GENERATED",
    text: "Pickup Generated",
  },
  {
    id: "READY_FOR_PICKUP",
    value: "READY_FOR_PICKUP",
    text: "Ready For Pickup",
  },
  {
    id: "PICKUP_COLLECTED",
    value: "PICKUP_COLLECTED",
    text: "Pickup Collected",
  },
  {
    id: "RECEIVE_IN_WAREHOUSE",
    value: "RECEIVE_IN_WAREHOUSE",
    text: "Received In Warehouse",
  },
  { id: "SHELVE", value: "SHELVE", text: "Shelve" },
  { id: "FORWARD_TO_3PL", value: "FORWARD_TO_3PL", text: "Forward To 3PL" },
  {
    id: "OUT_FOR_DELIVERY",
    value: "OUT_FOR_DELIVERY",
    text: "Out For Delivery",
  },
  { id: "REVERSE_NEW", value: "REVERSE_NEW", text: "Reverse New" },
  { id: "IN_TRANSIT", value: "IN_TRANSIT", text: "In Transit" },
  { id: "DELIVERED", value: "DELIVERED", text: "Delivered" },
  { id: "NOT_DELIVERED", value: "NOT_DELIVERED", text: "Not Delivered" },
  { id: "RETURNED", value: "RETURNED", text: "Returned" },
  { id: "CANCELLED", value: "CANCELLED", text: "Cancelled" },
  { id: "DELETED", value: "DELETED", text: "Deleted" },
  { id: "PENDING", value: "PENDING", text: "Pending" },
  {
    id: "RETURN_TO_SHIPPER",
    value: "RETURN_TO_SHIPPER",
    text: "Return To Shipper",
  },
];

interface ActionSidebarProps {
  onClose: () => void;
  selectedOrders: any[];
  setSelectedOrders: (orders: any[]) => void;
  orders: any[];
  refetch: () => void;
  onShowBulkPdf?: (orders: any[], companyName: string) => void;
  onShowSplitOrder?: (order: any) => void;
}

export default function ActionSidebar({
  onClose,
  selectedOrders,
  setSelectedOrders,
  orders,
  refetch,
  onShowBulkPdf,
  onShowSplitOrder,
}: ActionSidebarProps) {
  const { t } = useLanguage();
  const [awbText, setAwbText] = useState("");
  const [actionValue, setActionValue] = useState("");
  const [clientId, setClientId] = useState("");

  const translatedActionTypes = useMemo(() => ActionTypeData.map(item => ({ ...item, text: t(item.text) })), [t]);
  const translatedOrderStatusOptions = useMemo(() => ORDER_STATUS_OPTIONS.map(item => ({ ...item, text: t(item.text) })), [t]);

  const profile = useSelector((state: any) => state.auth.profile);

  // React Hook Form for Step 3 Action Details
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      companyId: "",
      warehouse: "",
      reason: "",
      targetStatus: "",
      requestType: "PICKUP",
      vehicleType: "car",
      locationId: "",
      pickupDate: "",
      timeSlot: "",
      noOfPieces: 1,
    },
  });

  // Fetch dropdown options
  const { data: shippingPartners } = use3PLListData();
  const { data: warehouses } = useWarehouseListData();
  const { data: clients } = useMerchantAllListData();
  const { data: clientAddresses } = usePickupLocationClientAddressData(
    clientId || "",
  );

  const locationList = useMemo(() => {
    if (!clientAddresses) return [];
    return Array.isArray(clientAddresses)
      ? clientAddresses
      : clientAddresses?.locations ||
          clientAddresses?.addresses ||
          clientAddresses?.data ||
          [];
  }, [clientAddresses]);

  // Mutations
  const { mutate: reverseOrderMutate, isPending: isReverseOrderPending } =
    useOrderReverseMutation();
  const { mutate: orderStatusRevertMutate, isPending: isStatusRevertPending } =
    useOrderStatusRevertStatusMutation();
  const { mutate: updateStatusMutate, isPending: isStatusUpdatePending } =
    useOrderStatusUpdateStatusMutation();

  const { mutate: orderForwardBulkMutate, isPending: isOrderForwardPending } =
    useOrderForwardBulkMutation();
  const { mutate: naqelBulkForwardMutate, isPending: isNaqelForwardPending } =
    useNaqelBulkForwardMutation();
  const { mutate: aramexBulkForwardMutate, isPending: isAramexForwardPending } =
    useAramexBulkForwardMutation();
  const { mutate: reverseNaqelOrderMutate, isPending: isNaqelReversePending } =
    useBulkReverseNaqelShipmentMutation();
  const {
    mutate: reverseAramexOrderMutate,
    isPending: isAramexReversePending,
  } = useAramexReverseShipmentMutation();
  const { mutate: generatePickupMutate, isPending: isPickupPending } =
    usePickRequestMutation();
  const { mutate: jtBulkForwardMutate, isPending: isJtForwardPending } =
    useJtBulkForwardMutation();
  const {
    mutateAsync: cancelJtOrderMutateAsync,
    isPending: isCancelJtPending,
  } = useCancelJtOrderMutation();

  const isSubmitting =
    isReverseOrderPending ||
    isStatusRevertPending ||
    isOrderForwardPending ||
    isNaqelForwardPending ||
    isAramexForwardPending ||
    isNaqelReversePending ||
    isAramexReversePending ||
    isPickupPending ||
    isJtForwardPending ||
    isCancelJtPending;

  // Sync AWBs from selected orders
  useEffect(() => {
    const awbs = selectedOrders
      .map((o) => o.awbNumber || o.referenceNumber)
      .filter(Boolean)
      .join("\n");
    setAwbText(awbs);
  }, [selectedOrders]);

  // Parse AWBs from input text
  const parsedAwbs = useMemo(() => {
    return awbText
      .split(/[\n, ]+/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }, [awbText]);

  // Find matching order objects from the active table list
  const matchedOrders = useMemo(() => {
    return parsedAwbs
      .map((awb) => {
        return orders.find(
          (o: any) => o.awbNumber === awb || o.referenceNumber === awb,
        );
      })
      .filter(Boolean);
  }, [parsedAwbs, orders]);

  // Lock number of pieces to 1 when multiple AWBs are pasted
  useEffect(() => {
    if (parsedAwbs.length > 1) {
      setValue("noOfPieces", 1);
    }
  }, [parsedAwbs.length, setValue]);

  // Auto-resolve client for Generate Pickup action
  useEffect(() => {
    if (actionValue !== "generatePickup") return;
    if (!matchedOrders.length) return;

    // 1. Try to resolve via o.clientId directly (most reliable)
    const clientIds = matchedOrders.map((o: any) => o.clientId).filter(Boolean);
    const uniqueIds = clientIds.filter(
      (value, index, self) => self.indexOf(value) === index,
    );

    if (uniqueIds.length === 1 && uniqueIds[0]) {
      const resolvedClientId = uniqueIds[0];
      setClientId(resolvedClientId);
      setValue("clientId", resolvedClientId);
      setValue("pickupType", "Shipment");
      return;
    }

    // 2. Fallback to client name matching (case-insensitive) if clientId is not directly on the order
    if (!clients) return;
    const clientNames = matchedOrders
      .map((o: any) => o.clientName)
      .filter(Boolean);
    const uniqueNames = clientNames.filter(
      (value, index, self) => self.indexOf(value) === index,
    );

    if (uniqueNames.length !== 1) return;

    const matchedClient = clients.find(
      (c: any) => c.name?.toLowerCase() === uniqueNames[0]?.toLowerCase(),
    );
    if (matchedClient?.id) {
      setClientId(matchedClient.id);
      setValue("clientId", matchedClient.id);
      setValue("pickupType", "Shipment");
    }
  }, [actionValue, matchedOrders, clients, setValue]);

  const handleSyncFromTable = () => {
    const awbs = selectedOrders
      .map((o) => o.awbNumber || o.referenceNumber)
      .filter(Boolean)
      .join("\n");
    setAwbText(awbs);
    toast.success("Synchronized AWBs from table selection.");
  };

  const onSubmit = (data: any) => {
    if (parsedAwbs.length === 0) {
      toast.error("Please enter at least one AWB number.");
      return;
    }

    if (actionValue === "statusRevert") {
      const payload = {
        targetStatus: data.targetStatus,
        reason: data.reason,
        awbs: parsedAwbs,
      };

      orderStatusRevertMutate(payload, {
        onSuccess: () => {
          toast.success("Status reverted successfully");
          setSelectedOrders([]);
          reset();
          refetch();
          onClose();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to revert status");
        },
      });
    } else if (actionValue === "statusUpdate") {
      const payload = {
        targetStatus: data.targetStatus,
        reason: data.reason,
        awbs: parsedAwbs,
      };

      updateStatusMutate(payload, {
        onSuccess: () => {
          toast.success("Status updated successfully");
          setSelectedOrders([]);
          reset();
          refetch();
          onClose();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update status");
        },
      });
    } else if (actionValue === "forward") {
      if (!matchedOrders.length) {
        toast.error(
          "AWB numbers must match orders currently visible in the table for forwarding",
        );
        return;
      }

      const warehouseIds = Array.from(
        new Set(matchedOrders.map((o) => o.warehouseId)),
      );
      if (warehouseIds.length > 1) {
        toast.error("Please select orders from the same warehouse");
        return;
      }

      const selectedWarehouseId = data.warehouse;
      if (
        selectedWarehouseId &&
        warehouseIds.length === 1 &&
        warehouseIds[0] !== selectedWarehouseId
      ) {
        toast.error(
          "The selected warehouse must match the warehouse of the selected orders",
        );
        return;
      }

      const selectedCompanyId = data.companyId;
      if (!selectedCompanyId) {
        toast.error("Please select a 3PL company");
        return;
      }

      const selected3pl = shippingPartners?.find(
        (p: any) => p.id === selectedCompanyId,
      );
      const companyName = selected3pl?.companyName?.toLowerCase() || "";

      // Status Validation
      const allowedStatuses = ["RECEIVE_IN_WAREHOUSE", "SHELVE", "IN_TRANSIT"];
      const invalidOrders = matchedOrders.filter(
        (o) => !allowedStatuses.includes(o.status),
      );
      if (invalidOrders.length > 0) {
        toast.error(
          "Only Receive in Warehouse, Shelve, and In Transit orders can be forwarded.",
        );
        return;
      }

      const payload = {
        companyId: selectedCompanyId,
        warehouse: selectedWarehouseId,
        noOfPieces: parsedAwbs.length > 1 ? 1 : Number(data.noOfPieces) || 1,
        orderIds: matchedOrders.map((o) => o.id),
        clientId: profile?.id,
      };

      if (
        companyName.includes("naqel") ||
        companyName.includes("naqel express") ||
        companyName.includes("Naqel Express")
      ) {
        naqelBulkForwardMutate(payload, {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "Naqel forward failed");
              return;
            }
            toast.success("Naqel forwarding in progress...");
            setSelectedOrders([]);
            setTimeout(() => {
              refetch();
              toast.success("Orders updated successfully");
            }, 3000);
            onClose();
          },
          onError: (error: any) => {
            toast.error(
              error?.response?.data?.message ||
                error?.message ||
                "Naqel forward failed",
            );
          },
        });
      } else if (companyName.includes("aramex")) {
        aramexBulkForwardMutate(
          {
            orderIds: matchedOrders.map((o) => o.id),
            noOfPieces: payload.noOfPieces,
            clientId: profile?.id,
            warehouse: payload.warehouse,
          },
          {
            onSuccess: (res: any) => {
              if (res?.success === false) {
                toast.error(res?.message || "Aramex forward failed");
                return;
              }
              toast.success("Aramex forwarding in progress...");
              setSelectedOrders([]);
              setTimeout(() => {
                refetch();
                toast.success("Orders updated successfully");
              }, 3000);
              onClose();
            },
            onError: (error: any) => {
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  "Aramex forward failed",
              );
            },
          },
        );
      } else if (
        companyName.includes("j&t") ||
        companyName.includes("jt express") ||
        companyName.includes("J&T Express")
      ) {
        jtBulkForwardMutate(
          {
            orderIds: matchedOrders.map((o) => o.id),
            clientId: profile?.id,
          },
          {
            onSuccess: (res: any) => {
              if (res?.success === false) {
                toast.error(res?.message || "J&T Express forward failed");
                return;
              }
              toast.success("J&T Express forwarding in progress...");
              setSelectedOrders([]);
              setTimeout(() => {
                refetch();
                toast.success("Orders updated successfully");
              }, 3000);
              onClose();
            },
            onError: (error: any) => {
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  "J&T Express forward failed",
              );
            },
          },
        );
      } else {
        orderForwardBulkMutate(payload, {
          onSuccess: (res: any) => {
            if (res?.success === false) {
              toast.error(res?.message || "Forward failed");
              return;
            }
            toast.success("Orders forwarding in progress...");
            setSelectedOrders([]);
            setTimeout(() => {
              refetch();
              toast.success("Orders updated successfully");
            }, 3000);
            onClose();
          },
          onError: (error: any) => {
            toast.error(
              error?.response?.data?.message ||
                error?.message ||
                "Forward failed",
            );
          },
        });
      }
    } else if (actionValue === "labelPrint") {
      if (!matchedOrders.length) {
        toast.error(
          "Please ensure entered AWBs match orders currently loaded in the table",
        );
        return;
      }
      const selectedCompanyId = data.companyId;
      const selected3pl = shippingPartners?.find(
        (p: any) => p.id === selectedCompanyId,
      );
      const companyName = selected3pl?.companyName?.toLowerCase() || "";

      if (onShowBulkPdf) {
        onShowBulkPdf(matchedOrders, companyName);
      }
      onClose();
    } else if (actionValue === "orderbulkpdf") {
      if (onShowBulkPdf) {
        onShowBulkPdf(matchedOrders.length > 0 ? matchedOrders : orders, "");
      }
      onClose();
    } else if (actionValue === "reverseOrder") {
      reverseOrderMutate(
        { orderId: parsedAwbs },
        {
          onSuccess: () => {
            toast.success("Orders reversed successfully");
            setSelectedOrders([]);
            refetch();
            onClose();
          },
          onError: () => {
            toast.error("Failed to reverse orders");
          },
        },
      );
    } else if (actionValue === "returnTo3PL") {
      if (!matchedOrders.length) {
        toast.error(
          "Entered AWBs must match orders currently loaded in the table",
        );
        return;
      }
      const selectedCompanyId = data.companyId;
      if (!selectedCompanyId) {
        toast.error("Please select a 3PL company");
        return;
      }
      const selected3pl = shippingPartners?.find(
        (p: any) => p.id === selectedCompanyId,
      );
      const companyName = selected3pl?.companyName?.toLowerCase() || "";

      if (
        companyName.includes("naqel") ||
        companyName.includes("naqel express")
      ) {
        reverseNaqelOrderMutate(
          {
            orderIds: matchedOrders.map((o) => o.id),
            clientId: profile?.id || "",
          },
          {
            onSuccess: (res: any) => {
              const results = res?.data || [];
              const succeeded = results.filter(
                (r: any) => r.status === "success",
              ).length;
              const failed = results.filter(
                (r: any) => r.status === "failed",
              ).length;

              if (failed > 0 && succeeded === 0) {
                const firstError = results.find(
                  (r: any) => r.status === "failed",
                );
                toast.error(
                  firstError?.error ||
                    res?.message ||
                    "All Naqel reverse shipments failed",
                );
                return;
              }
              if (succeeded > 0 && failed > 0) {
                toast.success(
                  `Partial success: ${succeeded} returned to Naqel, ${failed} failed`,
                );
                refetch();
                onClose();
                return;
              }
              if (succeeded > 0) {
                toast.success(
                  `${succeeded} orders returned to Naqel successfully`,
                );
                refetch();
                onClose();
              }
            },
            onError: (error: any) => {
              toast.error(
                error?.response?.data?.message ||
                  error?.message ||
                  "Naqel return failed",
              );
            },
          },
        );
      } else if (companyName.includes("aramex")) {
        if (matchedOrders.length !== 1) {
          toast.error("Please select only ONE order for Aramex return");
          return;
        }
        reverseAramexOrderMutate(
          {
            orderId: matchedOrders[0].id,
            clientId: profile?.id || "",
            noOfPieces: Number(data.noOfPieces) || 1,
          },
          {
            onSuccess: (res: any) => {
              const results = res?.data || [];
              const successCount = results.filter(
                (r: any) => r.status === "success",
              ).length;
              const failedCount = results.filter(
                (r: any) => r.status === "failed",
              ).length;

              if (failedCount > 0 && successCount === 0) {
                toast.error(
                  results[0]?.error || "All reverse shipments failed",
                );
                return;
              }
              if (successCount > 0 && failedCount > 0) {
                toast.success(
                  `Partial success: ${successCount} succeeded, ${failedCount} failed`,
                );
                return;
              }
              if (successCount > 0) {
                toast.success("Order returned to Aramex successfully");
              }
              refetch();
              onClose();
            },
            onError: (error: any) => {
              console.error(error);
              toast.error("Aramex return failed");
            },
          },
        );
      } else {
        toast.error("Return not supported for this 3PL");
      }
    } else if (actionValue === "generatePickup") {
      const selectedClientIds = matchedOrders
        .map((o) => o.clientId)
        .filter((v, i, self) => self.indexOf(v) === i);

      if (selectedClientIds.length !== 1) {
        toast.error("Please select orders from the same client");
        return;
      }

      const invalidStatusOrders = matchedOrders.filter(
        (o) => o.status !== "NEW_ORDER",
      );
      if (invalidStatusOrders.length > 0) {
        toast.error("Pickup can be generated only for NEW ORDER status");
        return;
      }

      if (!clientId) {
        toast.error("Client not resolved");
        return;
      }

      if (!data.pickupDate || !data.timeSlot) {
        toast.error("Pickup date & time required");
        return;
      }

      const payload: any = {
        clientId: clientId,
        requestType: data.requestType,
        pickupType: "Shipment",
        pickupDate: data.pickupDate,
        timeSlot: data.timeSlot,
        shipmentsId: matchedOrders.map((o) => o.id),
      };

      if (data.requestType === "PICKUP") {
        if (!data.vehicleType || !data.locationId) {
          toast.error("Vehicle & Location required");
          return;
        }
        payload.vehicleType = data.vehicleType;
        payload.locationId = data.locationId;
      }

      generatePickupMutate(payload, {
        onSuccess: () => {
          toast.success("Pickup generated successfully");
          setSelectedOrders([]);
          reset();
          refetch();
          onClose();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Pickup generation failed");
        },
      });
    } else if (actionValue === "splitOrder") {
      if (matchedOrders.length !== 1) {
        toast.error("Please specify exactly one order to split");
        return;
      }
      const orderToSplit = matchedOrders[0];
      if (orderToSplit.status !== "PENDING_STOCK") {
        toast.error("Only orders with Pending Stock status can be split");
        return;
      }
      if (onShowSplitOrder) {
        onShowSplitOrder(orderToSplit);
      }
      onClose();
    } else if (actionValue === "cancelForward3PL") {
      if (!matchedOrders.length) {
        toast.error("Please specify at least one order");
        return;
      }

      const selectedCompanyId = data.companyId;
      if (!selectedCompanyId) {
        toast.error("Please select a 3PL company");
        return;
      }

      const selected3pl = shippingPartners?.find(
        (p: any) => p.id === selectedCompanyId,
      );
      const companyName = selected3pl?.companyName?.toLowerCase() || "";
      const reason = data.reason || "Order cancelled by user";

      if (
        companyName.includes("j&t") ||
        companyName.includes("jt express") ||
        companyName.includes("j&t express")
      ) {
        const cancelPromises = matchedOrders.map((o: any) => {
          const rawId = o.awbNumber || o.id || "";
          const txlogisticId = rawId
            .replace(/[^a-zA-Z0-9]/g, "")
            .substring(0, 50);
          return cancelJtOrderMutateAsync({
            txlogisticId,
            reason,
            clientId: profile?.id,
          });
        });

        toast.promise(Promise.all(cancelPromises), {
          loading: "Cancelling orders with J&T Express...",
          success: () => {
            setSelectedOrders([]);
            reset();
            refetch();
            onClose();
            return "Successfully cancelled orders with J&T Express";
          },
          error: "Failed to cancel some or all orders with J&T Express",
        });
      } else {
        toast.error("Cancel forward not supported for this 3PL");
      }
    }
  };

  const parsedAwbsCount = parsedAwbs.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    className="flex flex-col bg-white/20 rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
    >
    

      <div className="flex items-center justify-between px-5 py-3.5  shrink-0">
        <div className="">
          <h2 className="text-lg font-bold text-[#212121]">{t("Order Actions")}</h2>
          <p className="text-xs text-gray-500">{t("Run bulk operations on AWBs")}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={isSubmitting || parsedAwbsCount === 0 || !actionValue}
            className="bg-[#FBD037] hover:bg-[#FBD037]/90 active:scale-95 disabled:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-[#000000] rounded-lg py-1.5 px-3.5 text-[13px] font-bold shadow-md transition-all duration-150 flex items-center gap-1.5"
          >
            {isSubmitting ? t("Processing...") : t("Submit Action")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
            title={t("Close")}
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* Body - Clean & Compact Single View (Yellow Themed accents) */}
      <div className="w-full py-4 px-5 bg-transparent flex-1 flex flex-col min-h-0 space-y-4 overflow-y-auto">
        {/* Section 1: Choose Action */}
        <div className="flex items-center gap-3 shrink-0">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[110px] shrink-0">
            {t("Action Type")}
          </label>
          <div className="flex-1 min-w-0">
            <NewSelectInput
              name="action"
              placeholder={t("Select Action Type")}
              value={actionValue}
              setValue={(val) => setActionValue(val || "")}
              selectOptions={translatedActionTypes}
              removeDiv
              fullWidthDropdown={true}
            />
          </div>
        </div>

        {/* Section 2: Action Configuration */}
        {actionValue && (
          <div className="pt-3 border-t border-slate-200/60 space-y-2 shrink-0">
            <div className="flex items-center gap-1.5 text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">
              <IconSettings size={13} className="text-[#FBD037]" />
              <span>
                {t(ActionTypeData.find((a) => a.value === actionValue)?.text || "")}{" "}
                {t("Configuration")}
              </span>
            </div>

            <div className="space-y-2">
              {/* 3PL Forward Form */}
              {actionValue === "forward" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("3PL Company")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="companyId"
                        placeholder={t("Select 3PL")}
                        selectOptions={
                          shippingPartners?.map((company: any) => ({
                            id: company?.id,
                            value: company?.id,
                            text: company?.companyName,
                          })) || []
                        }
                        register={register}
                        validation={{ required: t("3PL is required") }}
                        error={errors.companyId}
                        disabled={
                          !shippingPartners || shippingPartners.length === 0
                        }
                        value={watch("companyId")}
                        setValue={(val: string) => setValue("companyId", val)}
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Warehouse")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="warehouse"
                        placeholder={t("Select Warehouse")}
                        selectOptions={
                          warehouses?.warehouses?.map((warehouse: any) => ({
                            id: warehouse?.id,
                            value: warehouse?.id,
                            text: warehouse?.name,
                          })) || []
                        }
                        register={register}
                        validation={{ required: "Warehouse is required" }}
                        error={errors.warehouse}
                        disabled={
                          !warehouses?.warehouses ||
                          warehouses?.warehouses?.length === 0
                        }
                        value={watch("warehouse")}
                        setValue={(val: string) => setValue("warehouse", val)}
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0 pt-1.5">
                      {t("No. of Pieces")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0 relative">
                      <NewinputField
                        type="number"
                        name="noOfPieces"
                        placeholder="1"
                        register={register}
                        error={errors.noOfPieces}
                        validation={{ required: t("Required"), min: 1 }}
                        removeDiv
                        disabled={parsedAwbs.length > 1}
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      />
                      {parsedAwbs.length > 1 && (
                        <p className="text-[10px] text-amber-600 font-semibold mt-0.5">
                          {t("Locked to 1 piece when multiple AWBs are pasted.")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 3PL Return Form */}
              {actionValue === "returnTo3PL" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("3PL Company")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="companyId"
                        placeholder={t("Select 3PL")}
                        selectOptions={
                          shippingPartners?.map((company: any) => ({
                            id: company?.id,
                            value: company?.id,
                            text: company?.companyName,
                          })) || []
                        }
                        register={register}
                        validation={{ required: t("3PL is required") }}
                        error={errors.companyId}
                        value={watch("companyId")}
                        setValue={(val: string) => setValue("companyId", val)}
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Reason")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewinputField
                        type="text"
                        name="reason"
                        placeholder={t("Reason")}
                        register={register}
                        error={errors.reason}
                        validation={{ required: t("Reason is required") }}
                        removeDiv
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Forward Reverse Form */}
              {actionValue === "forwardReverse" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("3PL Company")}
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="companyId"
                        placeholder={t("Select 3PL")}
                        selectOptions={[]}
                        removeDiv
                        disabled
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Warehouse")}
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="warehouse"
                        placeholder={t("Select Warehouse")}
                        selectOptions={[]}
                        removeDiv
                        disabled
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 3PL Label Print Form */}
              {actionValue === "labelPrint" && (
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                    {t("3PL Company")} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex-1 min-w-0">
                    <NewSelectInput
                      name="companyId"
                      placeholder={t("Select 3PL")}
                      selectOptions={
                        shippingPartners?.map((company: any) => ({
                          id: company?.id,
                          value: company?.id,
                          text: company?.companyName,
                        })) || []
                      }
                      register={register}
                      validation={{ required: "3PL is required" }}
                      error={errors.companyId}
                      disabled={
                        !shippingPartners || shippingPartners.length === 0
                      }
                      value={watch("companyId")}
                      setValue={(val: string) => setValue("companyId", val)}
                      removeDiv
                      fullWidthDropdown={true}
                    />
                  </div>
                </div>
              )}

              {/* Status Revert Form */}
              {(actionValue === "statusRevert" ||
                actionValue === "statusUpdate") && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Target Status")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="targetStatus"
                        placeholder={t("Select Status")}
                        selectOptions={translatedOrderStatusOptions}
                        clearErrors={clearErrors}
                        register={register}
                        error={errors.targetStatus}
                        validation={{ required: t("Status is required") }}
                        value={watch("targetStatus")}
                        setValue={(val: string) =>
                          setValue("targetStatus", val)
                        }
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Reason")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewinputField
                        type="text"
                        name="reason"
                        placeholder={t("Enter Reason")}
                        register={register}
                        error={errors.reason}
                        validation={{ required: t("Reason is required") }}
                        removeDiv
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Pickup Form */}
              {actionValue === "generatePickup" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Request Type")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        placeholder={t("Select Type")}
                        name="requestType"
                        value={watch("requestType")}
                        register={register}
                        onChange={(val) => setValue("requestType", val)}
                        selectOptions={[
                          { id: "PICKUP", value: "PICKUP", text: t("PICKUP") },
                          { id: "DROPOFF", value: "DROPOFF", text: t("DROPOFF") },
                        ]}
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  {watch("requestType") === "PICKUP" && (
                    <>
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                          {t("Vehicle Type")} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1 min-w-0">
                          <NewSelectInput
                            placeholder={t("Select Vehicle")}
                            name="vehicleType"
                            register={register}
                            value={watch("vehicleType")}
                            onChange={(val) => setValue("vehicleType", val)}
                            selectOptions={[
                              { id: "car", value: "car", text: t("Car") },
                              { id: "van", value: "van", text: t("Van") },
                              { id: "dokker", value: "dokker", text: t("DoKker") },
                            ]}
                            removeDiv
                            fullWidthDropdown={true}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                          {t("Location")} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1 min-w-0">
                          <NewSelectInput
                            placeholder={t("Select Location")}
                            name="locationId"
                            register={register}
                            value={watch("locationId")}
                            onChange={(val) => setValue("locationId", val)}
                            selectOptions={locationList.map((loc: any) => ({
                              id: loc.id,
                              value: loc.id,
                              text:
                                loc.locationName ||
                                loc.fullAddress ||
                                loc.shortAddress ||
                                loc.full_address ||
                                loc.short_address ||
                                loc.address ||
                                "",
                            }))}
                            removeDiv
                            fullWidthDropdown={true}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Pickup Date")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewinputField
                        type="date"
                        name="pickupDate"
                        placeholder={t("Pickup Date")}
                        register={register}
                        error={errors.pickupDate}
                        validation={{ required: t("Date is required") }}
                        removeDiv
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E]"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("Time Slot")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewinputField
                        type="time"
                        name="timeSlot"
                        placeholder={t("Pickup Time")}
                        register={register}
                        error={errors.timeSlot}
                        validation={{ required: t("Time slot is required") }}
                        removeDiv
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* PDF Generate Static Info */}
              {actionValue === "orderbulkpdf" && (
                <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-100/60 leading-normal">                  {t("This will generate bulk airway bills for the matching AWBs specified above. Click Submit Action in the header to render the document.")}
</p>
              )}

              {/* Reverse Order Static Info */}
              {actionValue === "reverseOrder" && (
                <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-100/60 leading-normal">                  {t("This will create a reverse order for the specified AWBs. Are you sure you want to proceed?")}
</p>
              )}

              {/* Split Order Static Info */}
              {actionValue === "splitOrder" && (
                <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-100/60 leading-normal">                  {t("Splitting is only allowed if exactly one AWB is specified, and it has the status Pending Stock.")}
</p>
              )}

              {/* Cancel 3PL Forward Form */}
              {actionValue === "cancelForward3PL" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-[110px] shrink-0">
                      {t("3PL Company")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewSelectInput
                        name="companyId"
                        placeholder={t("Select 3PL")}
                        selectOptions={
                          shippingPartners?.map((company: any) => ({
                            id: company?.id,
                            value: company?.id,
                            text: company?.companyName,
                          })) || []
                        }
                        register={register}
                        validation={{ required: t("3PL is required") }}
                        error={errors.companyId}
                        value={watch("companyId")}
                        setValue={(val: string) => setValue("companyId", val)}
                        removeDiv
                        fullWidthDropdown={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-[#212121] w-[110px] shrink-0">
                      {t("Reason")} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1 min-w-0">
                      <NewinputField
                        type="text"
                        name="reason"
                        placeholder={t("Reason")}
                        register={register}
                        error={errors.reason}
                        validation={{ required: t("Reason is required") }}
                        removeDiv
                        className="space-y-0.5"
                        inputClassName="w-full rounded-lg py-1 px-3 text-sm outline-none bg-white border border-[#D8D8D8] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 text-[#1E1E1E]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 3: Paste AWB / Ref Numbers (Stretches to fill empty space dynamically) */}
        <div className="pt-3 border-t border-slate-200/60 flex-1 flex flex-col space-y-2 min-h-[165px]">
          <div className="flex justify-between items-center shrink-0">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
              {t("Paste AWB / Ref Numbers")}
            </label>
            <span className="text-[9px] font-bold text-[#131327] bg-[#FBD037]/20 border border-[#FBD037]/35 px-2 py-0.5 rounded-full shadow-sm shrink-0">
              {parsedAwbsCount} {parsedAwbsCount === 1 ? t("AWB") : t("AWBs")}
            </span>
          </div>

          <div className="flex-1 flex flex-col space-y-2 min-h-0">
            <textarea
              value={awbText}
              onChange={(e) => setAwbText(e.target.value)}
              placeholder={t("Enter AWB Number 1") + "\n" + t("Enter AWB Number 2") + "\n..."}
              className="w-full rounded-xl border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FBD037]/20 focus:border-[#FBD037] resize-none shadow-sm transition-all duration-200 bg-white text-[#1E293B] h-[110px] overflow-y-auto shrink-0"
            />

            {selectedOrders.length > 0 && (
              <div className="flex items-center justify-between text-[10px] text-slate-500 shrink-0">
                <span>{selectedOrders.length} {t("orders selected in table")}</span>
                <button
                  type="button"
                  onClick={handleSyncFromTable}
                  className="font-bold text-blue-600 hover:text-blue-700 hover:underline transition animate-pulse"
                >
                  {t("Sync selection")}
                </button>
              </div>
            )}

            {/* Validation summary display */}
            {parsedAwbs.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-y-auto border border-[#FBD037]/15 rounded-xl p-2.5 bg-white shadow-inner shrink-0">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("AWB Verification")}
                </span>
                {parsedAwbs.map((awb, i) => {
                  const order = orders.find(
                    (o: any) =>
                      o.awbNumber === awb || o.referenceNumber === awb,
                  );
                  return (
                    <div
                      key={`${awb}-${i}`}
                      className="flex items-center justify-between text-[11px] py-0.5 border-b border-dashed border-slate-100 last:border-0"
                    >
                      <span className="font-mono text-slate-500">{awb}</span>
                      {order ? (
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-100/50 px-1 rounded text-[8px] font-bold">
                          {t(order.status.toLowerCase().replace(/_/g, " ")) || order.status}
                        </span>
                      ) : (
                        <span className="bg-yellow-50 text-yellow-800 border border-yellow-100 px-1 rounded text-[8px] font-bold">
                          {t("Unmatched")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
